import React from 'react';
import type { PaginatedDataEntityKey, DataManagementSortOption } from '../../../utils/dataManagementListUtils';
import {
  getDataManagementSortLabel,
  hasActiveDataManagementFilters,
} from '../../../utils/dataManagementListUtils';

export type DataManagementFilterOption = {
  value: number;
  label: string;
};

export type DataManagementFiltersProps = {
  entityKey: PaginatedDataEntityKey;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  parentFilterId?: number;
  onParentFilterIdChange: (value: number | undefined) => void;
  parentFilterOptions: DataManagementFilterOption[];
  sort: DataManagementSortOption;
  onSortChange: (value: DataManagementSortOption) => void;
  expanded?: boolean;
  onExpand?: () => void;
};

const PARENT_FILTER_LABELS: Record<
  Exclude<PaginatedDataEntityKey, 'TopCategory'>,
  string
> = {
  SecondaryCategory: 'Üst Kategori',
  ProductType: 'Alt Kategori',
  SpecConstraint: 'Ürün Tipi',
};

const DataManagementFilters: React.FC<DataManagementFiltersProps> = ({
  entityKey,
  searchText,
  onSearchTextChange,
  parentFilterId,
  onParentFilterIdChange,
  parentFilterOptions,
  sort,
  onSortChange,
  expanded = true,
  onExpand,
}) => {
  const showParentFilter = entityKey !== 'TopCategory';
  const hasActiveFilters = hasActiveDataManagementFilters(searchText, parentFilterId, sort);
  const parentLabel =
    entityKey !== 'TopCategory' ? PARENT_FILTER_LABELS[entityKey] : null;
  const selectedParentLabel = parentFilterOptions.find((opt) => opt.value === parentFilterId)?.label;

  if (!expanded) {
    return (
      <button
        type="button"
        className="sb-data-management-toolbar-collapsed w-100 d-flex align-items-center gap-2 px-3 py-2 border-0 text-start"
        onClick={onExpand}
        aria-expanded={false}
        aria-controls="dm-filter-panel"
      >
        <i className="fas fa-filter text-primary" aria-hidden="true" />
        <span className="small fw-medium">Filtreler</span>
        {hasActiveFilters ? (
          <span className="sb-data-management-filter-summary small text-truncate">
            {searchText.trim() && (
              <span className="me-2">
                Ara: <strong>{searchText.trim()}</strong>
              </span>
            )}
            {parentFilterId != null && selectedParentLabel && parentLabel && (
              <span className="me-2">
                {parentLabel}: <strong>{selectedParentLabel}</strong>
              </span>
            )}
            {sort !== 'name_asc' && (
              <span>
                Sıra: <strong>{getDataManagementSortLabel(sort)}</strong>
              </span>
            )}
          </span>
        ) : (
          <span className="sb-data-management-meta small">Genişletmek için tıklayın</span>
        )}
        <i className="fas fa-chevron-down sb-data-management-meta ms-auto small" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div id="dm-filter-panel" className="sb-data-management-toolbar sb-data-management-toolbar-fields p-3">
      <div className="sb-data-management-field">
        <label className="form-label form-label-sm mb-1 text-body-secondary" htmlFor="dm-search">
          Ara
        </label>
        <input
          id="dm-search"
          type="search"
          className="form-control form-control-sm"
          placeholder="Metin ara..."
          value={searchText}
          onChange={(e) => onSearchTextChange(e.target.value)}
        />
      </div>

      {showParentFilter && (
        <div className="sb-data-management-field">
          <label className="form-label form-label-sm mb-1 text-body-secondary" htmlFor="dm-parent-filter">
            {PARENT_FILTER_LABELS[entityKey]}
          </label>
          <select
            id="dm-parent-filter"
            className="form-select form-select-sm"
            value={parentFilterId ?? ''}
            onChange={(e) =>
              onParentFilterIdChange(e.target.value ? Number(e.target.value) : undefined)
            }
          >
            <option value="">Tümü</option>
            {parentFilterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="sb-data-management-field">
        <label className="form-label form-label-sm mb-1 text-body-secondary" htmlFor="dm-sort">
          Sırala
        </label>
        <select
          id="dm-sort"
          className="form-select form-select-sm"
          value={sort}
          onChange={(e) => onSortChange(e.target.value as DataManagementSortOption)}
        >
          <option value="name_asc">Ada göre (A-Z)</option>
          <option value="name_desc">Ada göre (Z-A)</option>
          <option value="created_desc">Oluşturma (yeniden eskiye)</option>
          <option value="created_asc">Oluşturma (eskiden yeniye)</option>
        </select>
      </div>
    </div>
  );
};

export default DataManagementFilters;
